import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { cannotForwardMessageType } from '@/components/conversations/conversation-list/message-list-item/use-message-info';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useDeleteMessages from '@/hooks/conversations/use-delete-messages';
import ROUTES from '@/routes';
import type { MultiselectActionType } from '@/stores/conversation';

function ConversationMultiSelectBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type, id: sessionId, resetMultiselect, multiselectAction, selectedMessages } = useConversationStore();

  const { promptDeleteMessages } = useDeleteMessages(type, sessionId);

  const handleDeleteMessages = async () => {
    await promptDeleteMessages(selectedMessages)
      .then(() => {
        resetMultiselect();
      })
      .catch(console.warn);
  };

  const isGroup = type === 'group';
  const canPerformActions: MultiselectActionType[] = useMemo(() => {
    if (selectedMessages.length <= 0) return [];

    const actions: Set<MultiselectActionType> = new Set();

    if (multiselectAction === 'default' || multiselectAction === null) {
      actions.add('delete');
      actions.add('forward');
    } else {
      actions.add(multiselectAction);
    }

    if (
      actions.has('forward') &&
      selectedMessages.some((m) => m.ReadDelete || cannotForwardMessageType.has(m.MsgType))
    ) {
      actions.delete('forward');
    }

    return Array.from(actions.values());
  }, [selectedMessages, multiselectAction]);

  return (
    <div className='flex min-h-14 items-center flex-1 justify-between gap-2 px-5 py-3'>
      <button
        type='button'
        className='btn btn-md flex-1 border bg-neutral-0 desktop:min-h-10 desktop:h-10'
        onClick={() => resetMultiselect()}>
        {t('common.cancel')}
      </button>

      {(multiselectAction === 'default' || multiselectAction === 'forward') && (
        <button
          type='button'
          className='btn btn-primary btn-md flex-1 desktop:min-h-10 desktop:h-10'
          disabled={!canPerformActions.includes('forward')}
          onClick={() => {
            const route = isGroup ? ROUTES.MESSAGES.GROUPS.FORWARD : ROUTES.MESSAGES.SESSIONS.FORWARD;
            const messageIds = selectedMessages.map((m) => m.ID);
            resetMultiselect();
            navigate(
              route.$buildPath({
                params: { id: sessionId },
                searchParams: { messageIds },
              }),
            );
          }}>
          {t('conversation.forward.forward')}
          {`(${selectedMessages.length})`}
        </button>
      )}

      {(multiselectAction === 'default' || multiselectAction === 'delete') && (
        <button
          type='button'
          disabled={!canPerformActions.includes('delete')}
          className='btn btn-error btn-md flex-1 text-white desktop:min-h-10 desktop:h-10'
          onClick={() => handleDeleteMessages()}>
          {t('conversation.delete.delete')}
          {`(${selectedMessages.length})`}
        </button>
      )}
    </div>
  );
}

export default ConversationMultiSelectBar;
